import router from '@/router';
import { defineStore } from 'pinia';
import { fetchCompanies } from '@/services/companiesApi';
import { fetchDatasetOrder, createDatasetOrder, postContactForm } from '@/services/ppApi';
import { useScreenWidthStore } from '@/stores/utils/screenWidth';
import { popupBuilderDatasetLoaded } from '@/utils/popups';
import { gaBuilderResultsCount, gaBuilderFacets } from '@/utils/analytics/builder';
import { defaultCompanyQueryOptions, generateCompanyQueryParams } from '@/utils/builder';

export const useDatasetStore = defineStore('dataset', {
    state: () => {
        const defaultDataset = defaultCompanyQueryOptions;

        return {
            name: null,
            error: null,
            loading: false,
            defaultDataset: defaultDataset,
            dataset: JSON.parse(localStorage.getItem('bd_dataset')) || JSON.parse(JSON.stringify(defaultDataset)),
            backup: null,
            count: null,
            price: null,
            coupon: null,
            facets: null,
            companies: null,
            order: null,
            query: null,
            refs: {
                methodForm: null,
                orderForm: null,
                consultForm: null
            }
        };
    },
    getters: {
        hasDefaultDataset: (state) => JSON.stringify(state.dataset) === JSON.stringify(state.defaultDataset),
        changedParams: (state) => {
            const changes = {};
            changes.extra = state.dataset.extra;
            changes.location = state.dataset.location || {};
            
            Object.keys(state.defaultDataset).forEach((key) => {
                if (key === 'extra' || key === 'location') return;
                
                if (!(key in state.dataset)) {
                    const defVal = state.defaultDataset[key];
                    let nonEmpty = false;
                    if (typeof defVal === 'string') { nonEmpty = defVal.trim() !== ''; }
                    else if (Array.isArray(defVal)) { nonEmpty = defVal.length > 0; }
                    else if (defVal !== null && typeof defVal === 'object') { nonEmpty = Object.keys(defVal).length > 0; }
                    else { nonEmpty = !!defVal; }
                    
                    if (nonEmpty) {
                        changes[key] = defVal;
                    }
                } else if (JSON.stringify(state.dataset[key]) !== JSON.stringify(state.defaultDataset[key])) {
                    changes[key] = state.dataset[key];
                }
            });
            return changes;
        },
        finalPrice: (state) => {
            if (state.price && state.coupon?.discount) {
                return state.price * ((100 - Number(state.coupon.discount)) / 100);
            }
            return state.price;
        },
        shareLink: (state) => {
            try {
                const changedParams = {};
                Object.keys(state.dataset).forEach((key) => {
                    if (JSON.stringify(state.dataset[key]) !== JSON.stringify(state.defaultDataset[key])) {
                        if(key === 'sbi' || key === 'relations') {
                            changedParams[key] = {
                                includes: state.dataset[key].includes ? state.dataset[key].includes.map((item) => item.value) : [],
                                excludes: state.dataset[key].excludes ? state.dataset[key].excludes.map((item) => item.value) : []
                            }
                        } else if (key === 'text' && !state.dataset[key].custom?.length) {
                            changedParams[key] = {
                                includes: state.dataset[key].includes ? state.dataset[key].includes.map((item) => item.value) : [],
                                excludes: state.dataset[key].excludes ? state.dataset[key].excludes.map((item) => item.value) : [],
                                required: state.dataset[key].required ? state.dataset[key].required.map((item) => item.value) : [],
                                custom: state.dataset[key].custom || ''
                            }
                        } else if (key === 'apps') {
                            changedParams[key] = {
                                includes: state.dataset[key].includes ? state.dataset[key].includes.map(item => {
                                    // eslint-disable-next-line
                                    const { icon, ...other } = item;
                                    return other;
                                }) : [],
                                excludes: state.dataset[key].excludes ? state.dataset[key].excludes.map(item => {
                                    // eslint-disable-next-line
                                    const { icon, ...other } = item;
                                    return other;
                                }) : [],
                                required: state.dataset[key].required ? state.dataset[key].required.map(item => {
                                    // eslint-disable-next-line
                                    const { icon, ...other } = item;
                                    return other;
                                }) : []
                            }
                        } else {
                            changedParams[key] = state.dataset[key];
                        }
                    }
                });
                if (Object.keys(changedParams).length === 0) {
                    return false;
                }

                const baseUrl = process.env.VUE_APP_URL + '/dataset-controleren' || '';
                const datasetParam = encodeURIComponent(JSON.stringify(changedParams));
                return `${baseUrl}?bd_ds=${datasetParam}`;
            } catch (error) {
                return false;
            }
        }
    },
    actions: {
        saveDataset() {
            if(!this.hasDefaultDataset) {
                localStorage.setItem('bd_dataset', JSON.stringify(this.dataset));
            } else {
                localStorage.removeItem('bd_dataset');
            }
        },
        async updateDataset() {
            this.name = null;
            this.count = null;
            this.price = null;
            this.coupon = null;
            this.facets = null;
            this.companies = null;
            this.query = null;
            return await this.fetchDataset();
        },
        resetDataset() {
            this.$reset();
            this.dataset = JSON.parse(JSON.stringify(this.defaultDataset));
            this.saveDataset();
        },
        async overwriteDataset(dataset, name = null) {
            if(!this.hasDefaultDataset) {
                this.backup = JSON.parse(JSON.stringify(this.dataset));
            }
            
            this.dataset = JSON.parse(JSON.stringify({
                ...this.defaultDataset,
                ...dataset
            }));

            await this.updateDataset();

            this.name = name || null;

            const routeName = router.currentRoute.value.name;

            if(routeName === 'builder' || routeName === 'specification') {
                if(this.backup) {
                    popupBuilderDatasetLoaded(this.name, this.restoreBackup);
                } else {
                    popupBuilderDatasetLoaded(this.name);
                }
            }
        },
        deleteBackup() {
            this.backup = null;
        },
        restoreBackup() {
            if (this.backup) {
                this.dataset = JSON.parse(JSON.stringify(this.backup));
                this.backup = null;
                this.count = null;
                this.price = null;
                this.coupon = null;
                this.facets = null;
                this.companies = null;
                this.query = null;
                this.fetchDataset(true);
            }
        },
        async fetchDataset(includeFacets = false) {
            this.loading = true;
            this.error = null;

            const processedDataset = generateCompanyQueryParams(this.dataset);

            const facets = {
                facets: ['data_exists','orgtype','apps_cat','employees_range','revenue_range','province','sbi'].join(','),
                facets_ignore_exclude: true,
                facets_industry_summarized: true
            }

            const examples = {
                rows: 10
            }

            try {
                const payload =
                    includeFacets ? { ...processedDataset, ...facets, ...examples } : useScreenWidthStore().hasLargeScreen ? { ...processedDataset, ...examples } : processedDataset;

                payload.and_or = 'AND';

                const response = await fetchCompanies(payload);

                if(response.status >= 200 && response.status < 300) {
                    this.saveDataset();
                    this.count = response.data.found;
                    this.price = response.data.price;
                    this.query = response.data.query;
                    this.facets = response.data.facets || null;
                    this.companies = response.data.companies || null;
                    return true;
                } else {
                    this.count = null;
                    this.price = null;
                    this.facets = null;
                    this.companies = null;
                    this.query = null;
                    return false;
                }
            } catch (error) {
                this.count = null;
                this.price = null;
                this.facets = null;
                this.companies = null;
                this.query = null;
                this.error = error;
                return false;
            } finally {
                this.loading = false;

                if(includeFacets) {
                    gaBuilderFacets(!this.error, this.count, this.price);
                } else {
                    gaBuilderResultsCount(!this.error, this.count, this.price);
                }
            }
        },
        async fetchOrder(order_id) {
            this.loading = true;

            try {
                const response = await fetchDatasetOrder(order_id);

                if(response.status >= 200 && response.status < 300) {
                    this.order = response.data;
                }
            } catch (error) {
                this.error = error;
            } finally {
                this.loading = false;
            }
        },
        async createOrder(customerDetails) {
            try {                
                const payload = {
                    query: this.query,
                    coupon: this.coupon?.coupon ? this.coupon.coupon : '',
                    ...customerDetails
                };

                const response = await createDatasetOrder(payload);

                if(response.status >= 200 && response.status < 300) {
                    this.order = response.data;
                    return true;
                }
            } catch (error) {
                this.error = error;
                return false;
            }
        },
        async postContactForm(formData) {
            try {
                formData = { ...formData, ...{ share_link: this.shareLink || null, query: this.query } };
                const response = await postContactForm(formData);

                if(response.status >= 200 && response.status < 300) {
                    return true;
                } else {
                    return false;
                }
            } catch (error) {
                this.error = error;
                return false;
            }
        },
        setCoupon(coupon) {
            if(parseFloat(coupon.discount) > 0) {
                this.coupon = coupon;
            } else {
                this.coupon = {};
            }
        }
    }
});